import { Application, Container, Graphics } from 'pixi.js';
import { renderOnDemand, type OnDemandRendering } from '../../engine/onDemandPixi';
import { BoardCamera } from '../core/boardCamera';
import { ARROW_HIT_RADIUS, ARROW_TIP_LENGTH, arrowHitDistance } from '../core/arrowHitTest';
import { arrowExitDistance } from '../core/arrowExit';
import { ENTRANCE_DURATION, entranceDelay, entranceReveal, entranceZoom } from '../core/boardEntrance';
import type { ArrowData, Cell, Level, ThemeName } from '../core/types';

export const PALETTES: Record<ThemeName, { arrow: number; background: string; accent: string }> = {
  Default: { arrow: 0x9BF9E7, background: '#101528', accent: '#64EBCD' },
  Gold: { arrow: 0xFFE09B, background: '#1B1827', accent: '#FFD079' },
  Coral: { arrow: 0xFFB5CC, background: '#201529', accent: '#FF96B7' },
  Teal: { arrow: 0x8AE5FF, background: '#0D1D2B', accent: '#66D9FA' }
};
const dirVector: Record<ArrowData['direction'], Cell> = {
  UP: { x: 0, y: -1 }, DOWN: { x: 0, y: 1 }, LEFT: { x: -1, y: 0 }, RIGHT: { x: 1, y: 0 }
};
const center = (c: Cell): Cell => ({ x: c.x + .5, y: c.y + .5 });
const dist = (a: Cell, b: Cell) => Math.hypot(a.x - b.x, a.y - b.y);
const vector = (a: Cell, b: Cell): Cell => ({ x: b.x - a.x, y: b.y - a.y });
const plus = (a: Cell, b: Cell, s = 1): Cell => ({ x: a.x + b.x * s, y: a.y + b.y * s });

type Track = { points: Cell[]; lengths: number[]; total: number; headArc: number; exitArc: number };
type PieceView = { data: ArrowData; graphic: Graphics; track: Track; progress: number; reveal: number; revealDelay: number; sliding: boolean; exitEmitted: boolean; highlightUntil: number; wobbleUntil: number };
type EffectView = { graphic: Graphics; start: number; duration: number; initialAlpha: number; kind: 'pulse' | 'trail' | 'spark'; x: number; y: number };
type BoardPointer = { x: number; y: number; startX: number; startY: number; startClientX: number; startClientY: number };

function buildRoundedPoints(points: Cell[], radius = .12): Cell[] {
  if (points.length < 2) return points;
  const out: Cell[] = [points[0]!];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = points[i - 1], p = points[i], next = points[i + 1];
    const incoming = vector(prev!, p!), outgoing = vector(p!, next!);
    if (Math.abs(incoming.x - outgoing.x) + Math.abs(incoming.y - outgoing.y) < .01) { out.push(p!); continue; }
    const a = plus(p!, incoming, -radius), b = plus(p!, outgoing, radius);
    const middle = plus(a, outgoing, radius);
    const start = Math.atan2(a.y - middle.y, a.x - middle.x);
    const end = Math.atan2(b.y - middle.y, b.x - middle.x);
    let delta = end - start;
    while (delta > Math.PI) delta -= Math.PI * 2;
    while (delta < -Math.PI) delta += Math.PI * 2;
    out.push(a);
    for (let step = 1; step <= 9; step++) {
      const angle = start + delta * step / 9;
      out.push({ x: middle.x + radius * Math.cos(angle), y: middle.y + radius * Math.sin(angle) });
    }
  }
  out.push(points[points.length - 1]!);
  return out;
}
function findNearestArc(points: Cell[], lengths: number[], position: Cell): number {
  let closest = Infinity, answer = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1], b = points[i], dx = b!.x - a!.x, dy = b!.y - a!.y;
    const denom = dx * dx + dy * dy;
    const t = denom ? Math.max(0, Math.min(1, ((position.x - a!.x) * dx + (position.y - a!.y) * dy) / denom)) : 0;
    const p = { x: a!.x + t * dx, y: a!.y + t * dy };
    const d = dist(p, position);
    if (d < closest) { closest = d; answer = lengths[i - 1]! + t * Math.sqrt(denom); }
  }
  return answer;
}
function buildTrack(a: ArrowData, level: Level): Track {
  const direction = dirVector[a.direction];
  const cells = a.cells.map(center);
  const head = cells[cells.length - 1];
  for (let i = 1; i <= Math.max(level.width, level.height) + 18; i++) cells.push(plus(head!, direction, i));
  const points = buildRoundedPoints(cells);
  const lengths = [0];
  for (let i = 1; i < points.length; i++) lengths.push(lengths[i - 1]! + dist(points[i - 1]!, points[i]!));
  const headArc = findNearestArc(points, lengths, head!);
  return { points, lengths, total: lengths[lengths.length - 1]!, headArc, exitArc: headArc + arrowExitDistance(a, level) };
}
function at(track: Track, length: number): Cell {
  const l = Math.max(0, Math.min(track.total, length));
  let lo = 0, hi = track.lengths.length - 1;
  while (lo < hi) { const mid = (lo + hi) >>> 1; if (track.lengths[mid]! < l) lo = mid + 1; else hi = mid; }
  const i = Math.max(1, lo), len = track.lengths[i]! - track.lengths[i - 1]!;
  const t = len === 0 ? 0 : (l - track.lengths[i - 1]!) / len;
  const a = track.points[i - 1], b = track.points[i];
  return { x: a!.x + (b!.x - a!.x) * t, y: a!.y + (b!.y - a!.y) * t };
}
function sliced(track: Track, start: number, end: number): Cell[] {
  const out = [at(track, start)];
  for (let i = 1; i < track.points.length - 1; i++) {
    if (track.lengths[i]! > start && track.lengths[i]! < end) out.push(track.points[i]!);
  }
  out.push(at(track, end));
  return out;
}

export class BoardRenderer {
  readonly app = new Application();
  private root = new Container();
  private pieces = new Map<string, PieceView>();
  private effects: EffectView[] = [];
  private exitHandler: (() => void) | null = null;
  private level: Level | null = null;
  private camera = new BoardCamera();
  private pointers = new Map<number, BoardPointer>();
  private gestureMoved = false;
  private clickHandler: ((id: string) => void) | null = null;
  private arrowColor = PALETTES.Default.arrow;
  private hintColor = 0xFFDA75;
  private reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  private lastFrame = performance.now();
  private ready = false;
  private entrance: { elapsed: number; onComplete: () => void } | null = null;
  private guidedId: string | null = null;
  private guideRing: Graphics | null = null;
  private onDemand: OnDemandRendering | null = null;

  async init(host: HTMLElement): Promise<void> {
    await this.app.init({ resizeTo: host, backgroundAlpha: 0, antialias: true, resolution: Math.min(devicePixelRatio || 1, 2), autoDensity: true });
    // Draw only when the board changes: a still board costs no GPU or battery.
    this.onDemand = renderOnDemand(this.app);
    host.appendChild(this.app.canvas);
    this.app.stage.addChild(this.root);
    this.app.stage.eventMode = 'none';
    this.bindGestures();
    this.app.ticker.add(() => this.update());
    new ResizeObserver(() => this.resize()).observe(host);
    this.ready = true;
    this.resize(true);
  }
  setLevel(level: Level, remainingIds: string[], onTap: (id: string) => void, theme: ThemeName, onExit?: () => void): void {
    this.setGuide(null);
    this.entrance = null;
    this.cancelGesture();
    this.level = level;
    this.clickHandler = onTap;
    this.exitHandler = onExit ?? null;
    this.effects.forEach(e => e.graphic.destroy());
    this.effects = [];
    this.arrowColor = PALETTES[theme].arrow;
    this.pieces.forEach(p => p.graphic.destroy());
    this.pieces.clear();
    for (const arrow of level.arrows) {
      if (!remainingIds.includes(arrow.id)) continue;
      const g = new Graphics();
      this.root.addChild(g);
      const tail = arrow.cells[0];
      const piece: PieceView = { data: arrow, graphic: g, track: buildTrack(arrow, level), progress: 0, reveal: 1,
        revealDelay: entranceDelay(tail!.x, tail!.y, level.width, level.height), sliding: false, exitEmitted: false, highlightUntil: 0, wobbleUntil: 0 };
      this.pieces.set(arrow.id, piece);
      this.drawPiece(piece);
    }
    this.resize(true);
  }
  playEntrance(onComplete: () => void): void {
    this.cancelGesture();
    if (this.reducedMotion.matches || !this.pieces.size) { onComplete(); return; }
    this.entrance = { elapsed: 0, onComplete };
    this.lastFrame = performance.now();
    this.pieces.forEach(piece => { piece.reveal = 0; this.drawPiece(piece); });
    this.applyCamera();
  }
  setTheme(theme: ThemeName): void {
    this.arrowColor = PALETTES[theme].arrow;
    this.pieces.forEach(piece => this.drawPiece(piece));
  }
  slide(id: string): void {
    const piece = this.pieces.get(id);
    if (!piece) return;
    piece.sliding = true;
    if (!this.reducedMotion.matches) {
      const head = center(piece.data.cells[piece.data.cells.length - 1]!);
      const spark = new Graphics();
      this.root.addChild(spark);
      this.effects.push({ graphic: spark, start: performance.now(), duration: 380, initialAlpha: .9, kind: 'spark', x: head.x, y: head.y });
    }
    const trail = new Graphics();
    const points = sliced(piece.track, 0, piece.track.headArc);
    if (points.length && !this.reducedMotion.matches) {
      trail.moveTo(points[0]!.x, points[0]!.y);
      for (const p of points.slice(1)) trail.lineTo(p.x, p.y);
      trail.stroke({ color: this.arrowColor, width: .16, cap: 'round', join: 'round' });
      trail.alpha = .35;
      this.root.addChildAt(trail, 0);
      this.effects.push({ graphic: trail, start: performance.now(), duration: 400, initialAlpha: .35, kind: 'trail', x: 0, y: 0 });
    }
  }
  wobble(id: string): void { const piece = this.pieces.get(id); if (piece) piece.wobbleUntil = performance.now() + 280; }
  highlight(id: string): void {
    const piece = this.pieces.get(id);
    if (!piece) return;
    const tip = center(piece.data.cells[piece.data.cells.length - 1]!);
    this.camera.reveal(tip.x, tip.y);
    this.applyCamera();
    piece.highlightUntil = performance.now() + 1000;
  }
  setGuide(id: string | null): void {
    this.guidedId = id;
    this.guideRing?.destroy();
    this.guideRing = null;
    this.pieces.forEach(piece => this.drawPiece(piece));
    const piece = id ? this.pieces.get(id) : null;
    if (!piece) return;
    const tip = center(piece.data.cells[piece.data.cells.length - 1]!);
    this.guideRing = new Graphics().circle(tip.x, tip.y, .66).stroke({ color: this.hintColor, width: .035, alpha: .65 });
    this.root.addChild(this.guideRing);
    this.camera.reveal(tip.x, tip.y);
    this.applyCamera();
  }
  clear(): void { this.setGuide(null); this.entrance = null; this.cancelGesture(); this.effects.forEach(e => e.graphic.destroy()); this.effects = []; this.pieces.forEach(p => p.graphic.destroy()); this.pieces.clear(); this.level = null; }
  private drawPiece(piece: PieceView, now = performance.now()): void {
    const g = piece.graphic;
    g.clear();
    if (piece.reveal <= 0) return;
    const hint = now < piece.highlightUntil || piece.data.id === this.guidedId;
    g.alpha = this.guidedId && piece.data.id !== this.guidedId ? .4 : 1;
    const color = now < piece.wobbleUntil ? 0xFF668A : hint ? this.hintColor : piece.sliding ? 0xFFFFFF : this.arrowColor;
    const start = piece.progress;
    const movingHead = piece.track.headArc * piece.reveal + piece.progress;
    const end = Math.min(piece.track.exitArc, movingHead);
    const headVisible = movingHead + ARROW_TIP_LENGTH <= piece.track.exitArc;
    if (start >= end - .1) return;
    const pts = sliced(piece.track, start, Math.max(start, end - (headVisible ? .10 : 0)));
    if (!pts.length) return;
    g.moveTo(pts[0]!.x, pts[0]!.y);
    for (const p of pts.slice(1)) g.lineTo(p.x, p.y);
    g.stroke({ color, width: .16, cap: 'round', join: 'round' });
    // The tail follows the head through the outer edge. Internal gaps remain
    // part of the route; gameplay has checked all blockers along that route.
    if (!headVisible) return;
    const head = at(piece.track, end);
    const before = at(piece.track, Math.max(start, end - .12));
    const tangent = vector(before, head);
    const magnitude = Math.hypot(tangent.x, tangent.y) || 1;
    const dir = { x: tangent.x / magnitude, y: tangent.y / magnitude };
    const normal = { x: -dir.y, y: dir.x };
    const base = plus(head, dir, -.065), tip = plus(head, dir, ARROW_TIP_LENGTH);
    g.moveTo(tip.x, tip.y)
      .lineTo(base.x + normal.x * .21, base.y + normal.y * .21)
      .lineTo(base.x - normal.x * .21, base.y - normal.y * .21)
      .closePath().fill(color);
    if (hint) g.tint = 0xFFFFFF;
  }
  private update(): void {
    const now = performance.now();
    const dt = Math.min(.055, (now - this.lastFrame) / 1000);
    this.lastFrame = now;
    if (this.entrance) {
      // Only visible animation time counts, so backgrounding cannot skip the reveal.
      if (!document.hidden) this.entrance.elapsed += dt * 1000;
      const finished = this.reducedMotion.matches || this.entrance.elapsed >= ENTRANCE_DURATION;
      for (const piece of this.pieces.values()) {
        piece.reveal = finished ? 1 : entranceReveal(this.entrance.elapsed, piece.revealDelay);
        this.drawPiece(piece, now);
      }
      const onComplete = this.entrance.onComplete;
      if (finished) this.entrance = null;
      this.applyCamera();
      if (finished) onComplete();
      return;
    }
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const effect = this.effects[i];
      const fraction = Math.min(1, (now - effect!.start) / effect!.duration);
      if (fraction >= 1) { effect!.graphic.destroy(); this.effects.splice(i, 1); continue; }
      effect!.graphic.alpha = effect!.initialAlpha * (1 - fraction);
      if (effect!.kind === 'spark') {
        effect!.graphic.clear();
        for (let ray = 0; ray < 6; ray++) {
          const angle = ray * Math.PI / 3;
          const radius = .22 + fraction * .85;
          effect!.graphic.circle(effect!.x + Math.cos(angle) * radius, effect!.y + Math.sin(angle) * radius, .045 * (1 - fraction) + .015).fill(this.arrowColor);
        }
      }
      if (effect!.kind === 'pulse') {
        effect!.graphic.clear().circle(effect!.x, effect!.y, .10 + fraction * .55)
          .stroke({ color: this.arrowColor, width: .025 });
      }
    }
    for (const [id, piece] of this.pieces) {
      if (piece.sliding) {
        piece.progress += dt / .09;
        if (!piece.exitEmitted && piece.track.headArc + piece.progress >= piece.track.exitArc) {
          piece.exitEmitted = true;
          this.exitHandler?.();
        }
        if (piece.progress >= piece.track.exitArc) {
          piece.graphic.destroy(); this.pieces.delete(id); continue;
        }
      }
      piece.graphic.x = piece.wobbleUntil > now && !this.reducedMotion.matches ? Math.sin(now * .065) * .075 : 0;
      if (piece.sliding || piece.wobbleUntil > now || piece.highlightUntil > now || piece.graphic.x !== 0) this.drawPiece(piece, now);
      else if (piece.highlightUntil !== 0 || piece.wobbleUntil !== 0) {
        piece.highlightUntil = 0; piece.wobbleUntil = 0; this.drawPiece(piece, now);
      }
    }
  }
  private onPointer(x: number, y: number): void {
    if (!this.level || !this.clickHandler || this.entrance) return;
    const p = this.camera.toWorld(x, y);
    if (p.x < 0 || p.y < 0 || p.x > this.level.width || p.y > this.level.height) return;
    let selected: string | null = null, closest = ARROW_HIT_RADIUS;
    for (const [id, piece] of this.pieces) {
      if (piece.sliding) continue;
      const distance = arrowHitDistance({ x: p.x - piece.graphic.x, y: p.y }, piece.data);
      if (distance < closest) { selected = id; closest = distance; }
    }
    if (selected) {
      if (!this.reducedMotion.matches) {
        const pulse = new Graphics();
        this.root.addChild(pulse);
        this.effects.push({ graphic: pulse, start: performance.now(), duration: 300, initialAlpha: .45, kind: 'pulse', x: p.x, y: p.y });
      }
      this.clickHandler(selected);
    }
  }
  private applyCamera(): void {
    const zoom = this.entrance ? entranceZoom(this.entrance.elapsed) : 1;
    this.root.scale.set(this.camera.scale * zoom);
    this.root.position.set(this.camera.width / 2 + (this.camera.x - this.camera.width / 2) * zoom,
      this.camera.height / 2 + (this.camera.y - this.camera.height / 2) * zoom);
  }
  private cancelGesture(): void {
    const ids = [...this.pointers.keys()];
    this.pointers.clear();
    this.gestureMoved = true;
    const canvas = this.app.canvas;
    for (const id of ids) if (canvas.hasPointerCapture(id)) canvas.releasePointerCapture(id);
    canvas.classList.remove('dragging');
  }
  private bindGestures(): void {
    const canvas = this.app.canvas;
    // Measure finger jitter in CSS pixels, independently of resolution/zoom.
    const withinTapSlop = (event: PointerEvent, start: BoardPointer) =>
      Math.hypot(event.clientX - start.startClientX, event.clientY - start.startClientY) <= (event.pointerType === 'touch' ? 12 : 6);
    const point = (event: PointerEvent | WheelEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: (event.clientX - rect.left) * this.app.screen.width / rect.width,
        y: (event.clientY - rect.top) * this.app.screen.height / rect.height };
    };
    canvas.addEventListener('pointerdown', event => {
      if (!this.level || this.entrance || (event.pointerType === 'mouse' && event.button !== 0)) return;
      event.preventDefault();
      const p = point(event);
      if (!this.pointers.size) this.gestureMoved = false;
      else this.gestureMoved = true;
      this.pointers.set(event.pointerId, { ...p, startX: p.x, startY: p.y, startClientX: event.clientX, startClientY: event.clientY });
      canvas.setPointerCapture(event.pointerId);
    });
    canvas.addEventListener('pointermove', event => {
      const previous = this.pointers.get(event.pointerId);
      if (!previous) return;
      const next = point(event);
      if (this.pointers.size === 1) {
        if (!withinTapSlop(event, previous)) this.gestureMoved = true;
        if (this.gestureMoved) this.camera.pan(next.x - previous.x, next.y - previous.y);
      } else if (this.pointers.size === 2) {
        const other = [...this.pointers.entries()].find(([id]) => id !== event.pointerId)![1];
        const oldDistance = Math.hypot(previous.x - other.x, previous.y - other.y);
        const newDistance = Math.hypot(next.x - other.x, next.y - other.y);
        const cx = (previous.x + other.x) / 2, cy = (previous.y + other.y) / 2;
        if (oldDistance > 0) this.camera.zoomAt(this.camera.zoom * newDistance / oldDistance, cx, cy);
        this.camera.pan((next.x - previous.x) / 2, (next.y - previous.y) / 2);
      }
      this.pointers.set(event.pointerId, { ...previous, ...next });
      canvas.classList.toggle('dragging', this.gestureMoved);
      this.applyCamera();
    });
    const end = (event: PointerEvent) => {
      const previous = this.pointers.get(event.pointerId);
      if (!previous) return;
      const p = point(event);
      const tap = event.type === 'pointerup' && this.pointers.size === 1 && !this.gestureMoved &&
        p.x >= 0 && p.x <= this.camera.width && p.y >= 0 && p.y <= this.camera.height &&
        withinTapSlop(event, previous);
      this.pointers.delete(event.pointerId);
      if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
      if (event.type !== 'pointerup') this.gestureMoved = true;
      if (!this.pointers.size) canvas.classList.remove('dragging');
      // A finger can roll away from a thin arrow while lifting. Keep the
      // original touch target; deliberate drags and pinches are excluded above.
      if (tap) this.onPointer(event.pointerType === 'touch' ? previous.startX : p.x,
        event.pointerType === 'touch' ? previous.startY : p.y);
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('lostpointercapture', end);
    canvas.addEventListener('wheel', event => {
      if (!this.level) return;
      event.preventDefault();
      if (this.entrance) return;
      const p = point(event);
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.camera.height : 1;
      this.camera.zoomAt(this.camera.zoom * Math.exp(-event.deltaY * unit * (event.ctrlKey ? .01 : .002)), p.x, p.y);
      this.applyCamera();
    }, { passive: false });
    window.addEventListener('blur', () => this.cancelGesture());
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.cancelGesture(); });
  }
  private resize(reset = false): void {
    if (!this.ready || !this.level) return;
    const host = this.app.canvas.parentElement;
    if (!host) return;
    const width = host.clientWidth, height = host.clientHeight;
    const level = this.level;
    // Pixi's resizeTo reacts to window resizes, not every host layout change.
    // Tips/HUD changes can resize this flex item without a window resize.
    if (this.app.screen.width !== width || this.app.screen.height !== height) this.app.renderer.resize(width, height);
    this.camera.resize(width, height, level.width, level.height, reset);
    this.applyCamera();
    // A resized canvas is blank until the next frame, even when the scene did not move.
    this.onDemand?.requestRender();
  }
}
